/**
 * DEV-2392. Последние фильтры списков «Сделки» и «Клиенты» — в localStorage, по разделу
 * и пользователю. Раздел, открытый без query (из меню, новой вкладкой, после входа),
 * берёт их отсюда; ссылка с query всегда важнее.
 *
 * Номер страницы не храним: через день он указывает за край выборки. Разбор значений
 * остаётся за списком — те же проверки, что и для query из URL.
 */
import type { LocationQuery } from 'vue-router'

export type MemorizedList = 'deals' | 'clients'

const KEY_PREFIX = 'crm-list-filters:'
const storageKey = (list: MemorizedList, userId: string) => `${KEY_PREFIX}${list}:${userId}`

export function saveListQuery(
  list: MemorizedList,
  userId: string | undefined,
  query: Record<string, string | string[]>,
): void {
  if (!userId) return
  const filters = { ...query }
  delete filters.page
  try {
    localStorage.setItem(storageKey(list, userId), JSON.stringify(filters))
  } catch {
    /* приватный режим или квота — без памяти фильтров список всё равно работает */
  }
}

/** Сохранённый query или null, если его нет, он пуст или битый. */
export function loadListQuery(
  list: MemorizedList,
  userId: string | undefined,
): LocationQuery | null {
  if (!userId) return null
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(list, userId)) ?? 'null')
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const query: LocationQuery = {}
    for (const [name, value] of Object.entries(parsed)) {
      if (typeof value === 'string') query[name] = value
      else if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        query[name] = value
      }
    }
    return Object.keys(query).length ? query : null
  } catch {
    return null
  }
}

/**
 * Выход из CRM: в поиске бывают имена и телефоны клиентов, поэтому на общем
 * компьютере память фильтров не должна пережить сеанс. Чистим записи всех пользователей.
 */
export function clearListQueries(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key?.startsWith(KEY_PREFIX)) localStorage.removeItem(key)
    }
  } catch {
    /* хранилище недоступно — и чистить нечего */
  }
}
